'use strict';

/**
 * PlansheetCommentProvider
 * ------------------------
 * Adapts plansheet's CommentThread/Comment REST providers to the data seam that pict-section-comments
 * expects (its CommentDataProvider interface). The section calls neutral methods; this maps them to
 * the server rows and back, resolving author display names from the team roster / the signed-in user.
 *
 * It extends the section's CommentDataProvider base, so loadContext / resolveThread / completeThread /
 * reopenThread come for free on top of the eight primitives implemented here.
 */

const libCommentProvider = require('pict-section-comments');

class PlansheetCommentProvider extends libCommentProvider.CommentDataProvider
{
	constructor(pPict)
	{
		super({});
		this.pict = pPict;
	}

	_threadAPI() { return this.pict.providers.CommentThreadAPI; }
	_commentAPI() { return this.pict.providers.CommentAPI; }
	_unwrap(pResult) { return (pResult && pResult.Record) ? pResult.Record : pResult; }

	_ts(pDate)
	{
		if (!pDate) { return null; }
		let tmpValue = (typeof pDate === 'number') ? pDate : Date.parse(pDate);
		return isNaN(tmpValue) ? null : tmpValue;
	}

	// Resolve an author's display name + avatar from the signed-in user or the cached team roster.
	_author(pIDUser)
	{
		let tmpKey = pIDUser || 0;
		let tmpAuth = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || null;
		if (tmpAuth && String(tmpAuth.IDUser) === String(tmpKey))
		{
			return { Key: tmpKey, Name: tmpAuth.DisplayName || tmpAuth.Email || 'You', Avatar: tmpAuth.AvatarURL || '' };
		}
		let tmpTeam = this.pict.AppData.Team || {};
		let tmpPeople = [].concat(tmpTeam.Roster || [], tmpTeam.Members || []);
		let tmpHit = tmpPeople.find((pPerson) => String(pPerson.IDUser) === String(tmpKey));
		if (tmpHit)
		{
			let tmpName = tmpHit.DisplayName
				|| ([tmpHit.NameFirst, tmpHit.NameLast].filter(Boolean).join(' ') || '').trim()
				|| tmpHit.Email || ('User ' + tmpKey);
			return { Key: tmpKey, Name: tmpName, Avatar: tmpHit.AvatarURL || tmpHit.PhotoURL || '' };
		}
		return { Key: tmpKey, Name: tmpKey ? ('User ' + tmpKey) : 'Someone', Avatar: '' };
	}

	_toThread(pRow)
	{
		let tmpAnchor = null;
		if (pRow.AnchorJSON)
		{
			try { tmpAnchor = (typeof pRow.AnchorJSON === 'string') ? JSON.parse(pRow.AnchorJSON) : pRow.AnchorJSON; }
			catch (pError) { tmpAnchor = null; }
		}
		return {
			Key: pRow.IDCommentThread,
			OwnerType: pRow.OwnerType,
			IDOwner: pRow.IDOwner,
			Anchor: tmpAnchor,
			Kind: pRow.Kind || 'Discussion',
			Title: pRow.Title || '',
			Status: pRow.Status || 'Open',
			Sort: pRow.SortOrder || 0,
			Author: this._author(pRow.CreatingIDUser),
			CreatedAt: this._ts(pRow.CreateDate) || 0,
			UpdatedAt: this._ts(pRow.UpdateDate) || 0
		};
	}

	_toComment(pRow)
	{
		let tmpCreated = this._ts(pRow.CreateDate) || 0;
		let tmpUpdated = this._ts(pRow.UpdateDate) || 0;
		return {
			Key: pRow.IDComment,
			ThreadKey: pRow.IDCommentThread,
			ParentKey: pRow.IDParentComment || null,
			Author: this._author(pRow.IDUser || pRow.CreatingIDUser),
			Body: pRow.Body || '',
			ContentType: pRow.ContentType || 'text',
			CreatedAt: tmpCreated,
			UpdatedAt: tmpUpdated,
			EditedAt: (tmpUpdated && tmpCreated && tmpUpdated > tmpCreated + 1000) ? tmpUpdated : null,
			Deleted: !!pRow.Deleted
		};
	}

	// ---- the eight primitives ----

	listThreads(pContext)
	{
		return this._threadAPI().listForOwner(pContext.OwnerType, pContext.IDOwner)
			.then((pRows) => (pRows || []).map((pRow) => this._toThread(pRow)));
	}

	createThread(pDraft)
	{
		let tmpContext = pDraft.Context || pDraft;
		let tmpRow =
		{
			OwnerType: tmpContext.OwnerType,
			IDOwner: tmpContext.IDOwner,
			Kind: pDraft.Kind || 'Discussion',
			Title: pDraft.Title || '',
			Status: pDraft.Status || 'Open',
			SortOrder: pDraft.Sort || 0,
			AnchorJSON: pDraft.Anchor ? JSON.stringify(pDraft.Anchor) : ''
		};
		return this._threadAPI().create(tmpRow).then((pResult) => this._toThread(this._unwrap(pResult)));
	}

	updateThread(pKey, pPatch)
	{
		let tmpRow = {};
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Status')) { tmpRow.Status = pPatch.Status; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Title')) { tmpRow.Title = pPatch.Title; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Kind')) { tmpRow.Kind = pPatch.Kind; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Sort')) { tmpRow.SortOrder = pPatch.Sort; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Anchor')) { tmpRow.AnchorJSON = pPatch.Anchor ? JSON.stringify(pPatch.Anchor) : ''; }
		return this._threadAPI().update(pKey, tmpRow).then((pResult) => this._toThread(this._unwrap(pResult)));
	}

	deleteThread(pKey) { return this._threadAPI().remove(pKey); }

	listComments(pThreadKey)
	{
		return this._commentAPI().listForThread(pThreadKey)
			.then((pRows) => (pRows || []).map((pRow) => this._toComment(pRow)));
	}

	createComment(pDraft)
	{
		let tmpRow =
		{
			IDCommentThread: pDraft.ThreadKey,
			IDParentComment: pDraft.ParentKey || 0,
			Body: pDraft.Body,
			ContentType: pDraft.ContentType || 'text',
			IDUser: (pDraft.Author && pDraft.Author.Key) || 0,
			SortOrder: 0
		};
		return this._commentAPI().create(tmpRow).then((pResult) => this._toComment(this._unwrap(pResult)));
	}

	updateComment(pKey, pPatch)
	{
		let tmpRow = {};
		if (Object.prototype.hasOwnProperty.call(pPatch, 'Body')) { tmpRow.Body = pPatch.Body; }
		if (Object.prototype.hasOwnProperty.call(pPatch, 'ContentType')) { tmpRow.ContentType = pPatch.ContentType; }
		return this._commentAPI().update(pKey, tmpRow).then((pResult) => this._toComment(this._unwrap(pResult)));
	}

	deleteComment(pKey) { return this._commentAPI().remove(pKey); }
}

module.exports = PlansheetCommentProvider;
